import React, { useEffect, useState } from 'react';
import { getTaggingInformationScreen } from '../../utils/Tagging';
import { Container, InputsContainer } from './styles';
import { sendTagging } from '../../store/ducks/tagging/actions';
import { useDispatch, useSelector } from 'react-redux';
import {
  loadCustomerBillets,
  loadCustomerNfsAndQualityCertificates
} from '../../store/ducks/download-area/action';
import { ApplicationState } from '../../store';
import { Button, Title } from '@gerdau/hefesto';
import { DateField } from '@gerdau/hefesto-mais-web/DateField';
import { DateRangeType } from '@gerdau/hefesto-mais-web/DateField/DateField.types';
import CustomerSelect from '../../components/CustomerSelect/Facelift';
import moment from 'moment';
import DownloadAreaTabs from './DownloadAreaTabs';
import { addToast } from '../../store/ducks/toast/actions';
import { EToastTypes } from '../../store/ducks/toast/types';
import { StateZeroContainer } from './DownloadAreaTabs/styles';
import { ECustomerTypeView } from '../../store/ducks/user/types';
import IsManagerCustomerInput from '../../components/IsManagerCustomerInput';

const DownloadArea = () => {
  const { screenName = '', itemDescription = '' } =
    getTaggingInformationScreen('download-area');

  const dispatch = useDispatch();

  const downloadAreaState = useSelector(
    (appState: ApplicationState) => appState.downloadArea
  );

  const customersState = useSelector(
    (state: ApplicationState) => state.customers
  );

  const userState = useSelector((appState: ApplicationState) => appState.user);

  const searchCustomerType = useSelector(
    (appState: ApplicationState) => appState.user.searchCustomerType
  );
  const customerTypeDesc =
    searchCustomerType === ECustomerTypeView.EMITTER ? 'Emissor' : 'Recebedor';

  const [customerId, setCustomerId] = useState(downloadAreaState.customerId);

  const getInitialDateRange = (): DateRangeType => {
    const getDate = (date: string) => {
      return date !== '' ? moment(date, 'YYYY-MM-DD').toDate() : null;
    };
    return [
      getDate(downloadAreaState.startDate),
      getDate(downloadAreaState.endDate)
    ];
  };

  const [dateRange, setDateRange] = useState<DateRangeType>(
    getInitialDateRange()
  );

  useEffect(() => {
    dispatch(
      sendTagging({
        screenName,
        page: window.location.pathname,
        event: 'page_view'
      })
    );
  }, []);

  const getFormatedDates = (): { startDate: string; endDate: string } => {
    let startDate = '';
    let endDate = '';

    if (dateRange?.[0] && dateRange?.[1]) {
      startDate = moment(dateRange[0]).format('YYYY-MM-DD');
      endDate = moment(dateRange[1]).format('YYYY-MM-DD');
    }
    return { startDate, endDate };
  };

  const loadNFsAndQualityCertificates = () => {
    setIsShowingNfQcToastError(false);
    const { startDate, endDate } = getFormatedDates();
    dispatch(
      loadCustomerNfsAndQualityCertificates(
        customerId,
        startDate,
        endDate,
        searchCustomerType
      )
    );
  };

  const loadBillets = () => {
    setIsShowingBilletsToastError(false);
    const { startDate, endDate } = getFormatedDates();
    const formatCustomerId =
      userState.isManager && customerId && customerId.length < 10
        ? `0${customerId}`
        : customerId;
    dispatch(
      loadCustomerBillets(
        formatCustomerId,
        searchCustomerType,
        startDate,
        endDate
      )
    );
  };

  const loadAll = () => {
    if (!!customerId && dateRange?.[0] && dateRange?.[1]) {
      const { financialCustomers, ordersViewCustomers } = userState.roles;

      const canViewFinancials =
        financialCustomers.includes(customerId) || userState.isManager;
      const canViewOrders =
        ordersViewCustomers.includes(customerId) || userState.isManager;

      if (canViewOrders) {
        loadNFsAndQualityCertificates();
      }

      if (canViewFinancials) {
        loadBillets();
      }
    }
  };

  const [isShowingNfQcToastError, setIsShowingNfQcToastError] = useState(false);

  useEffect(() => {
    if (
      !isShowingNfQcToastError &&
      (downloadAreaState.nfsState.error ||
        downloadAreaState.qualityCertificatesState.error)
    ) {
      setIsShowingNfQcToastError(true);
      dispatch(
        addToast({
          message: 'Erro na busca de documentos. Tente novamente mais tarde',
          type: EToastTypes.DANGER,
          labelActionTwo: 'Tentar novamente',
          handleClickActionTwo: loadNFsAndQualityCertificates
        })
      );
    }
  }, [
    downloadAreaState.nfsState.error,
    downloadAreaState.qualityCertificatesState.error,
    isShowingNfQcToastError
  ]);

  const [isShowingBilletsToastError, setIsShowingBilletsToastError] =
    useState(false);

  useEffect(() => {
    if (!isShowingBilletsToastError && downloadAreaState.billetsState.error) {
      setIsShowingBilletsToastError(true);
      dispatch(
        addToast({
          message: 'Erro na busca de documentos. Tente novamente mais tarde',
          type: EToastTypes.DANGER,
          labelActionTwo: 'Tentar novamente',
          handleClickActionTwo: loadBillets
        })
      );
    }
  }, [downloadAreaState.billetsState.error, isShowingBilletsToastError]);

  const datePickerMaxDate = () => {
    if (dateRange?.[0]) {
      return moment(dateRange?.[0]).add(3, 'months').toDate();
    }
  };

  const datePickerMinDate = () => {
    if (dateRange?.[0]) {
      return moment(dateRange?.[0]).subtract(3, 'months').toDate();
    }

    if (dateRange?.[1]) {
      return moment(dateRange?.[1]).subtract(3, 'months').toDate();
    }
  };

  const handleDateChange = (date: DateRangeType) => {
    if (date && date[0] && date[1]) {
      if (moment(date[0]).isAfter(moment(date[1]))) {
        setDateRange([date[0], null]);
        return;
      }

      if (moment(date[0]).isBefore(moment(date[1]).subtract(3, 'months'))) {
        setDateRange([date[0], null]);
        return;
      }
    }
    setDateRange(date);
  };

  return (
    <>
      <Container>
        <header>
          <Title level={1}>Buscar documentos</Title>
          <span className="subtitle">
            Selecione um contrato e encontre os documentos relacionados aos
            pedidos
          </span>
        </header>

        <InputsContainer>
          <div className="customer-select">
            {userState.isManager ? (
              <IsManagerCustomerInput
                title="Emissor"
                customerId={customerId}
                setCustomerId={setCustomerId}
                loading={
                  customersState.loading ||
                  downloadAreaState.billetsState.loading ||
                  downloadAreaState.nfsState.loading ||
                  downloadAreaState.qualityCertificatesState.loading
                }
                bpError={false}
              />
            ) : (
              <CustomerSelect
                title={customerTypeDesc}
                placeholder={`- ${customerTypeDesc} -`}
                customers={customersState.customers.filter((customer) => {
                  const { financialCustomers, ordersViewCustomers } =
                    userState.roles;

                  const canViewFinancials = financialCustomers.includes(
                    customer.customerId
                  );
                  const canViewOrders = ordersViewCustomers.includes(
                    customer.customerId
                  );

                  return canViewFinancials || canViewOrders;
                })}
                disabled={
                  customersState.loading ||
                  downloadAreaState.billetsState.loading ||
                  downloadAreaState.nfsState.loading ||
                  downloadAreaState.qualityCertificatesState.loading
                }
                selectedCustomerId={customerId}
                onSelect={(selectedCustomerId) =>
                  setCustomerId(selectedCustomerId)
                }
              />
            )}
          </div>

          <div className="date-field">
            <DateField
              label="Período"
              name="download-area-date"
              helperText="Selecione o período de emissão em um intervalo de 3 meses"
              selectsRange={true}
              range={dateRange}
              onChange={handleDateChange}
              disabled={
                downloadAreaState.billetsState.loading ||
                downloadAreaState.nfsState.loading ||
                downloadAreaState.qualityCertificatesState.loading
              }
              allowPartialRange={true}
              maxDate={datePickerMaxDate()}
              minDate={datePickerMinDate()}
            />
          </div>
          <Button
            name="search-download-area"
            disabled={
              customersState.loading ||
              !customerId ||
              !dateRange ||
              !dateRange?.[0] ||
              !dateRange?.[1] ||
              downloadAreaState.billetsState.loading ||
              downloadAreaState.nfsState.loading ||
              downloadAreaState.qualityCertificatesState.loading
            }
            loading={
              downloadAreaState.billetsState.loading ||
              downloadAreaState.nfsState.loading ||
              downloadAreaState.qualityCertificatesState.loading
            }
            onClick={() => {
              dispatch(
                sendTagging({
                  screenName,
                  itemName: 'button:buscar',
                  itemDescription: `${itemDescription}_buscar`,
                  filterInfo: `#codEmr:${
                    customerId ? customerId : 'not_set'
                  }#dtInicio:${
                    dateRange?.[0]
                      ? moment(dateRange[0]).format('yyyy_MM_DD')
                      : 'not_set'
                  }#dtFim:${
                    dateRange?.[1]
                      ? moment(dateRange[1]).format('yyyy_MM_DD')
                      : 'not_set'
                  }`,
                  event: 'click'
                })
              );
              loadAll();
            }}
          >
            Buscar
          </Button>
        </InputsContainer>
      </Container>

      {downloadAreaState.customerId !== '' ? (
        <DownloadAreaTabs />
      ) : (
        <StateZeroContainer>
          <span>
            <b>Selecione um {customerTypeDesc.toLowerCase()}</b> para exibir os
            resultados.
          </span>
        </StateZeroContainer>
      )}
    </>
  );
};

export default DownloadArea;
